// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Document Viewer (full page, read-only)
// Mounts DocumentPanel with readOnly=true for the /documento/:codigo route
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { fetchDocumento } from '../../../../shared/services/api';
import { DocumentPanel } from '../components/DocumentPanel';
import { useActiveDocument } from '../../../../../context/ActiveDocumentContext';
import type { Documento } from '../../../../../types';

export function DocumentViewerPage() {
  const { t } = useI18n();
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<Documento | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [redirected, setRedirected] = useState(false);
  const {
    setActiveDocument,
    clearActiveDocument,
    codigoDocumento: currentActiveCodigo,
  } = useActiveDocument();

  useEffect(() => {
    if (codigo && doc?.titulo) {
      setActiveDocument(codigo, doc.titulo);
    }
    return () => {
      if (currentActiveCodigo === codigo) clearActiveDocument();
    };
  }, [codigo, doc?.titulo, currentActiveCodigo, setActiveDocument, clearActiveDocument]);

  const loadDoc = useCallback(async () => {
    if (!codigo) return;
    setLoading(true);
    try {
      const res = await fetchDocumento(codigo);
      setDoc(res.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error loading document');
    } finally {
      setLoading(false);
    }
  }, [codigo]);

  useEffect(() => {
    loadDoc();
  }, [loadDoc]);

  // Redirect to level 3 if doc has a process
  useEffect(() => {
    if (!doc || redirected) return;
    const procesoId = (doc.proceso as Record<string, unknown> | undefined)?.id as
      | string
      | undefined;
    if (procesoId) {
      setRedirected(true);
      navigate(`/process-map/process/${procesoId}?doc=${encodeURIComponent(doc.codigo)}`, {
        replace: true,
      });
    }
  }, [doc, navigate, redirected]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <LoadingSpinner text={t.documentViewer.cargandoDocumento} size="lg" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          color: '#EF4444',
        }}
      >
        {error || t.documentViewer.documentoNoEncontrado}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <DocumentPanel documento={doc} readOnly viewerMode useContentPages />
    </div>
  );
}

export default DocumentViewerPage;
